// Product zero's browser analytics. The project token is public and grants
// ingestion only; never put a personal API key in this client-side config.
import project from "./posthog.json" with { type: "json" };

export const POSTHOG = project;
export type AnalyticsSurface = "website" | "app";

const EVENTS = new Set(["$pageview", "$identify", "website_access_requested", "website_sign_in_clicked", "plugin_repository_opened", "plugin_install_commands_copied", "app_opened"]);
// Keep SDK identity and device metadata, but discard DOM text, person
// properties, persisted URL history and any future SDK properties by default.
const PROPERTIES = new Set([
  "token", "distinct_id", "$device_id", "$session_id", "$window_id", "$anon_distinct_id", "$user_id", "$is_identified", "$process_person_profile",
  "$lib", "$lib_version", "$browser", "$browser_version", "$os", "$os_version", "$device_type",
  "$screen_height", "$screen_width", "$viewport_height", "$viewport_width", "$timezone", "$timezone_offset", "$pageview_id",
]);
// A page's tab (`?view=`); the default tab sends none. Add a tab here when it
// ships, or its use is never seen (design/plan.md, 功能架构评估第三批).
const VIEWS = new Set(["overview", "actions", "table", "readings", "metrics", "definitions", "sources", "money", "facts", "lessons", "review", "content", "product", "guide", "playbooks", "channels", "rules", "closed", "goals", "needs_you", "next", "suggested", "handled"]);
/** How a page lays out the same objects, chosen per device (the campaigns' list or timeline). */
const LAYOUTS = new Set(["list", "timeline"]);

/** Only production sends by default. Validation is explicit and marked on every event. */
export function analyticsEnvironment(hostname: string, validation = false): "production" | "validation" | null {
  if (hostname === "zooi.ai" || hostname === "app.zooi.ai") return "production";
  if (validation && (["localhost", "127.0.0.1"].includes(hostname) || hostname.endsWith(".zooi-ai.workers.dev"))) return "validation";
  return null;
}

/** Page types, never object IDs, invitation tokens, arbitrary paths or URL queries. */
export function analyticsPath(surface: AnalyticsSurface, pathname: string): string | null {
  if (surface === "website") return pathname === "/" || pathname === "/zh/" ? pathname : "/404";
  if (["/", "/inbox", "/playbooks", "/new"].includes(pathname)) return pathname;
  if (/^\/playbooks\/[^/]+$/.test(pathname)) return "/playbooks/:playbook";
  const inbox = /^\/inbox\/(work-orders|notices|posts|outreach|readings|facts)\/[^/]+$/.exec(pathname);
  if (inbox) return `/inbox/${inbox[1]}/:item`;
  if (/^\/settings\/(members|tokens|preferences|gate)\/?$/.test(pathname)) return pathname.replace(/\/$/, "");
  const settings = /^\/settings\/products\/[^/]+(\/(agent|grants))?$/.exec(pathname);
  if (settings) return `/settings/products/:product${settings[1] ?? ""}`;
  const product = /^\/p\/[^/]+\/(week|campaigns|actions|data|profile|plans)(?:\/([^/]+))?(\/reading)?$/.exec(pathname);
  if (!product) return null;
  return `/p/:product/${product[1]}${product[2] ? `/:${product[1] === "week" ? "date" : "item"}` : ""}${product[3] ?? ""}`;
}

interface AnalyticsEvent { event: string; properties: Record<string, unknown> }

export function sanitizeAnalyticsEvent<T extends AnalyticsEvent>(event: T | null): T | null {
  if (!event || !EVENTS.has(event.event)) return null;
  const source = event.properties;
  const surface: AnalyticsSurface = source.surface === "website" ? "website" : "app";
  const path = typeof source.$pathname === "string" ? analyticsPath(surface, source.$pathname) : null;
  if (!path) return null;
  const properties: Record<string, unknown> = {};
  for (const key of PROPERTIES) if (key in source) properties[key] = source[key];
  properties.surface = surface;
  properties.environment = source.environment === "validation" ? "validation" : "production";
  properties.$pathname = path;
  properties.$host = surface === "website" ? "zooi.ai" : "app.zooi.ai";
  properties.$current_url = `https://${properties.$host}${path}`;
  if (typeof source.view === "string" && VIEWS.has(source.view)) properties.view = source.view;
  if (typeof source.layout === "string" && LAYOUTS.has(source.layout)) properties.layout = source.layout;
  if (source.agent === "claude" || source.agent === "codex") properties.agent = source.agent;
  if (["nav", "hero", "closing", "footer", "install"].includes(String(source.placement))) properties.placement = source.placement;
  if (surface === "website") {
    // Attribution values are bounded campaign labels; never keep full referrer URLs or ad click IDs.
    for (const key of ["utm_source", "utm_medium", "utm_campaign"]) {
      if (typeof source[key] === "string" && /^[a-zA-Z0-9_.-]{1,100}$/.test(source[key])) properties[key] = source[key];
    }
    if (typeof source.$referrer === "string") {
      try {
        const referrer = new URL(source.$referrer);
        if (referrer.protocol === "https:" || referrer.protocol === "http:") {
          properties.$referrer = referrer.origin;
          properties.$referring_domain = referrer.hostname;
        }
      } catch { /* A direct visit has no referrer URL. */ }
    }
  }
  event.properties = properties;
  return event;
}

export const POSTHOG_OPTIONS = {
  api_host: project.api_host,
  ui_host: project.ui_host,
  defaults: "2026-08-30" as const,
  autocapture: false,
  capture_pageview: false,
  capture_pageleave: false,
  capture_dead_clicks: false,
  rageclick: false,
  capture_heatmaps: false,
  capture_exceptions: false,
  capture_performance: false,
  disable_session_recording: true,
  disable_surveys: true,
  disable_product_tours: true,
  disable_external_dependency_loading: true,
  advanced_disable_flags: true,
  person_profiles: "identified_only" as const,
  persistence: "localStorage+cookie" as const,
  cross_subdomain_cookie: true,
  cookie_expiration: 30,
  secure_cookie: true,
  respect_dnt: true,
  mask_all_text: true,
  mask_all_element_attributes: true,
  save_referrer: false,
  save_campaign_params: false,
  before_send: sanitizeAnalyticsEvent,
};
